import { useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import { WarningIcon } from '@phosphor-icons/react';

import { changeComposeLanguage } from '@/mastodon/actions/compose';
import { Callout } from '@/mastodon/components/callout/redesign';
import { Icon } from '@/mastodon/components/icon';
import { useDismissible } from '@/mastodon/hooks/useDismissible';
import { selectAccountStatus } from '@/mastodon/selectors/statuses';
import {
  createAppSelector,
  useAppDispatch,
  useAppSelector,
} from '@/mastodon/store';

import classes from '../styles.module.scss';

import { languageName, useLanguageGuess } from './hooks';
import { selectComposeAttachments, selectComposePrivacy } from './selectors';

const selectComposeAttachmentsWithoutAlt = createAppSelector(
  [selectComposeAttachments],
  (attachments) => ({
    count: attachments.length,
    missingAlt: attachments.some((attachment) => !attachment.description),
  }),
);

const selectIsFollowersReply = createAppSelector(
  [
    (state) =>
      selectAccountStatus(
        state,
        state.compose.get('in_reply_to') as null | string,
      ),
    (state) => state.meta.get('me') as string | null,
  ],
  (status, me) =>
    status?.visibility === 'private' && status.account.id !== me
      ? status.account.acct
      : null,
);

export const ComposeHints = (props: React.ComponentPropsWithRef<'div'>) => {
  const { count, missingAlt } = useAppSelector(
    selectComposeAttachmentsWithoutAlt,
  );
  const replyFollowersHandle = useAppSelector(selectIsFollowersReply);
  const privacy = useAppSelector(selectComposePrivacy);

  const lang = useAppSelector(
    (state) => state.compose.get('language') as string,
  );
  const guess = useLanguageGuess();
  const isDifferentLanguage = lang && guess && lang !== guess;

  const publishErrors = useAppSelector((state) => state.composer.publishErrors);

  const messages: React.ReactNode[] = [];

  if (publishErrors.includes('empty')) {
    messages.push(
      <ComposerError key='empty'>
        <FormattedMessage
          id='compose.hints.empty'
          defaultMessage="Post can't be blank"
        />
      </ComposerError>,
    );
  }

  if (publishErrors.includes('too-long')) {
    messages.push(
      <ComposerError key='too-long'>
        <FormattedMessage
          id='compose.hints.too-long'
          defaultMessage='Post exceeds character maximum'
        />
      </ComposerError>,
    );
  }

  // Creatodon 独自の公開範囲を選んでいる場合の注意
  if (privacy === 'portfolio') {
    messages.push(
      defaultWrapper(
        <FormattedMessage
          id='compose_form.portfolio_warning'
          defaultMessage='公開範囲が「ポートフォリオ」になっています。ここで入力した内容はすべてアカウントの「ポートフォリオ」欄に表示されます'
        />,
        'portfolio-warning',
      ),
    );
  } else if (privacy === 'nyan') {
    messages.push(
      defaultWrapper(
        <FormattedMessage
          id='compose_form.nyan_warning'
          defaultMessage='公開範囲が「にゃーん」になっています。ここで入力した内容はすべて「にゃーん」に置き換えられます'
        />,
        'nyan-warning',
      ),
    );
  }

  if (replyFollowersHandle) {
    messages.push(
      defaultWrapper(
        <FormattedMessage
          id='compose.hints.followers-reply'
          defaultMessage="You're replying to a followers-only post. People not following {user} might see your reply without the context of what you’re replying to."
          values={{ user: `@${replyFollowersHandle}` }}
        />,
        'followers-reply',
      ),
    );
  }

  const { wasDismissed } = useDismissible('compose_language_hint');
  if (isDifferentLanguage && !wasDismissed) {
    messages.push(<LanguageHint guess={guess} key='different-language' />);
  }

  if (missingAlt && count === 1) {
    messages.push(
      defaultWrapper(
        <FormattedMessage
          id='compose.hints.missing-alt'
          defaultMessage='Your attachment is missing alt text.'
        />,
        'missing-alt',
      ),
    );
  } else if (missingAlt) {
    messages.push(
      defaultWrapper(
        <FormattedMessage
          id='compose.hints.missing-alt-many'
          defaultMessage='One or more of your attachments are missing alt text.'
        />,
        'missing-alts',
      ),
    );
  }

  if (messages.length === 0) {
    return null;
  }

  return (
    <div {...props} className={classes.calloutWrapper}>
      {messages}
    </div>
  );
};

const defaultWrapper = (children: React.ReactNode, key: string) => (
  <Callout icon={WarningIcon} key={key}>
    {children}
  </Callout>
);

const ComposerError: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div className={classes.calloutError}>
      <Icon icon={WarningIcon} />

      {children}
    </div>
  );
};

const LanguageHint: React.FC<{ guess: string }> = ({ guess }) => {
  const language = languageName(guess);

  const { wasDismissed, dismiss } = useDismissible('compose_language_hint');

  const dispatch = useAppDispatch();
  const handleChange = useCallback(() => {
    dispatch(changeComposeLanguage(guess));
  }, [dispatch, guess]);

  if (!language || wasDismissed) {
    return null;
  }

  return (
    <Callout
      icon={WarningIcon}
      actionClick={handleChange}
      actionText={
        <FormattedMessage
          id='compose.hints.language.change'
          defaultMessage='Change'
          description='Label on button to accept language change prompt'
        />
      }
      secondaryActionClick={dismiss}
      secondaryActionText={
        <FormattedMessage
          id='compose.hints.language.dismiss'
          defaultMessage='Dismiss'
          description='Label on button to dismiss language change prompt'
        />
      }
    >
      <FormattedMessage
        id='compose.hints.language'
        defaultMessage='Change this post’s language to {language}?'
        values={{ language }}
      />
    </Callout>
  );
};
